import React, { useState } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  Code,
  Smartphone,
  Globe,
  Users,
  Target,
  Zap,
  CheckCircle,
  ArrowRight,
  Rocket,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
} from "lucide-react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import logo from "./assets/images/logo_fk.png";

const FlowKraftWebsite = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState("");

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");

    // Validation basique
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
      return;
    }

    try {
      // Configuration EmailJS
      // IMPORTANT: Remplacez ces valeurs dans le fichier .env
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      // Préparer les paramètres du template
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_name: "FlowKraft Agency",
      };

      // Envoyer l'email via EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      // Succès
      setFormStatus("success");

      // Réinitialiser le formulaire
      setFormData({
        name: "",
        email: "",
        message: "",
      });

      setTimeout(() => setFormStatus(""), 5000);
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
    }
  };

  const services = [
    {
      icon: <Code className="w-12 h-12" />,
      title: "Développement Web",
      slug: "developpement-web",
      description:
        "Applications web modernes avec React, Vue.js, et technologies de pointe",
      features: [
        "Progressive Web Apps",
        "E-commerce",
        "CMS personnalisés",
        "API REST/GraphQL",
      ],
    },
    {
      icon: <Smartphone className="w-12 h-12" />,
      title: "Développement Mobile",
      slug: "developpement-mobile",
      description: "Applications natives et cross-platform pour iOS et Android",
      features: [
        "React Native",
        "Flutter",
        "Applications natives",
        "UI/UX optimisé",
      ],
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: "Sites Web Professionnels",
      slug: "developpement-web",
      description: "Sites vitrines et corporate responsive et optimisés SEO",
      features: [
        "Design responsive",
        "Optimisation SEO",
        "Performance optimale",
        "Maintenance incluse",
      ],
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Design UX/UI",
      slug: "design-ux-ui",
      description:
        "Conception d'expériences utilisateur innovantes et interfaces intuitives",
      features: [
        "Audit UX",
        "Prototypage interactif",
        "Design system",
        "Tests utilisateurs",
      ],
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: "Marketing Digital",
      slug: "marketing-digital",
      description:
        "Stratégies digitales pour augmenter votre visibilité en ligne",
      features: [
        "SEO/SEA",
        "Content Marketing",
        "Social Media",
        "Analytics & Reporting",
      ],
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Consulting & Stratégie",
      slug: "consulting",
      description:
        "Accompagnement stratégique pour votre transformation digitale",
      features: [
        "Audit technique",
        "Conseil en architecture",
        "Formation équipes",
        "Optimisation process",
      ],
    },
  ];

  const processSteps = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Découverte & Analyse",
      description:
        "Audit complet de vos besoins, objectifs business et contraintes techniques",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Stratégie & Planification",
      description:
        "Définition de la roadmap projet, choix technologiques et allocation des ressources",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Design & Prototypage",
      description:
        "Création de maquettes interactives et validation du parcours utilisateur",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Développement Agile",
      description:
        "Cycles itératifs de développement avec points réguliers et livrables incrementaux",
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Tests & Qualité",
      description:
        "Tests fonctionnels, performance, sécurité et recette utilisateur",
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Déploiement & Support",
      description:
        "Mise en production, formation utilisateurs et maintenance évolutive",
    },
  ];

  const projects = [
    {
      title: "SalonHub",
      description:
        "Plateforme de réservation en ligne pour salons de coiffure et instituts de beauté",
      // tech: ["React", "Node.js", "MongoDB"],
      image: "/images/dev-web/1.png",
      link: "https://salonhub.flowkraftagency.com",
      fallbackEmoji: "💇",
    },
    {
      title: "TeamShuffle",
      description:
        "Application de gestion d'équipes et d'organisation de tournois sportifs",
      // tech: ["React", "Firebase", "Tailwind CSS"],
      image: "/images/dev-mobile/miniature.jpeg",
      link: "https://teamshuffle.flowkraftagency.com",
      fallbackEmoji: "⚽",
    },
    {
      title: "MyFrame",
      description:
        "Solution de gestion de cadres photo numériques et galeries personnalisées",
      // tech: ["React", "Next.js", "AWS"],
      image: "/images/dev-web/myframe.png",
      link: "https://app.myframe.flowkraftagency.com",
      fallbackEmoji: "🖼️",
    },
  ];

  const testimonials = [
    {
      name: "Roland GBETANOU",
      company: "",
      text: "FlowKraft Agency m'a accompagné dans la refonte complète de mon identité digitale. Résultat : Site plus clair, tunnel optimisé, +47% de conversion en 30 jours. Vous avez simplifié notre business",
      rating: 5,
    },
    {
      name: "Flasolia",
      company: "",
      text: "J'ai été très satisfaite du travail que FLOWKRAFT AGENCY m'a rendu. J'étais très impressionnée par le travail en équipe qui a été fait ainsi que le rendu final.",
      rating: 5,
    },
    {
      name: "Gilles-Christ G.",
      company: "GCSGC Agency",
      text: "Un travail d'une grande qualité ! J'ai été impressionné par le professionnalisme et la créativité de l'équipe. Depuis la refonte, notre site web a retrouvé tout son dynamisme et attire beaucoup plus de visiteurs.",
      rating: 5,
    },
  ];

  return (
    <div className="font-sans">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/main/15652.jpg)" }}
        ></div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-hero opacity-80"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-8">
            <img
              src={logo}
              alt="FlowKraft Agency"
              className="h-32 w-auto mx-auto mb-6"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            FlowKraft Agency
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-3xl mx-auto">
            Votre partenaire technologique pour vos projets digitaux
          </p>
          <p className="text-lg md:text-xl text-flowkraft-secondary mb-8 max-w-2xl mx-auto">
            Développement web & mobile • Design UX/UI • Marketing Digital •
            Consulting & Stratégie
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="#contact"
              className="bg-flowkraft-primary hover:bg-flowkraft-secondary text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              Démarrer un projet
            </a>
            <a
              href="#services"
              className="border-2 border-white hover:bg-white hover:text-flowkraft-primary text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300"
            >
              Nos services
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nos Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une gamme complète de services digitaux pour accompagner votre
              croissance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-flowkraft-primary hover:shadow-flowkraft-lg transition-all duration-300 group"
              >
                <div className="text-flowkraft-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircle className="w-4 h-4 text-flowkraft-primary mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link
                    to={`/services/${service.slug}`}
                    className="inline-flex items-center text-flowkraft-primary hover:text-flowkraft-secondary font-semibold transition-colors"
                  >
                    En savoir plus <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/main/121215.jpg)" }}
        ></div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black to-flowkraft-primary opacity-90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Notre Méthodologie
            </h2>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Une méthodologie éprouvée en 6 étapes pour des projets réussis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-full p-6 mx-auto mb-4 w-20 h-20 flex items-center justify-center hover:bg-opacity-20 transition-all duration-300">
                  <div className="text-white">{step.icon}</div>
                </div>
                <div className="text-2xl font-bold text-flowkraft-secondary mb-2">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-300 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Nos Réalisations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez quelques-uns des projets qui ont marqué notre parcours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-flowkraft-xl transition-shadow duration-300"
              >
                <div className="h-48 bg-gradient-subtle flex items-center justify-center overflow-hidden">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-6xl">{project.fallbackEmoji}</span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  {/* <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, idx) => (
                      <span
                        key={idx}
                        className="bg-flowkraft-primary bg-opacity-10 text-flowkraft-primary px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div> */}
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-flowkraft-primary hover:text-flowkraft-secondary font-semibold transition-colors"
                    >
                      Voir le projet <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Nos partenaires et clients qui nous accompagnent dans notre
              croissance
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 items-center">
            {/* Partner logos */}
            {[
              { name: "Amazon", image: "/images/partners/amazon.svg" },
              { name: "DA", image: "/images/partners/da.svg" },
              { name: "House", image: "/images/partners/house.svg" },
              { name: "LWS", image: "/images/partners/lws.svg" },
            ].map((partner, index) => (
              <div
                key={index}
                className="flex items-center justify-center p-6 transition-all duration-300 group"
              >
                <img
                  src={partner.image}
                  alt={partner.name}
                  className="w-40 h-40 object-contain opacity-50 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110"
                  style={{
                    filter:
                      "brightness(0) saturate(100%) invert(59%) sepia(13%) saturate(1034%) hue-rotate(236deg) brightness(91%) contrast(88%)",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Témoignages Clients
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              La satisfaction de nos clients est notre priorité
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-flowkraft-primary"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-flowkraft-primary text-sm">
                    {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/main/4256.jpg)" }}
        ></div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Contactez-Nous
              </h2>
              <p className="text-xl text-gray-200">
                Prêt à transformer votre idée en réalité ?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Nos Coordonnées
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <svg
                      className="w-6 h-6 text-flowkraft-secondary mt-1 mr-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <div>
                      <p className="text-white font-semibold">Adresse</p>
                      <p className="text-gray-200">Toulouse, France</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <svg
                      className="w-6 h-6 text-flowkraft-secondary mt-1 mr-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <div>
                      <p className="text-white font-semibold">Email</p>
                      <a
                        href="mailto:info@flowkraftagency.com"
                        className="text-gray-200 hover:text-flowkraft-secondary transition-colors"
                      >
                        info@flowkraftagency.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <svg
                      className="w-6 h-6 text-flowkraft-secondary mt-1 mr-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <div>
                      <p className="text-white font-semibold">Téléphone</p>
                      <a
                        href="tel:+33652794562"
                        className="text-gray-200 hover:text-flowkraft-secondary transition-colors"
                      >
                        +33 (0)6 52 79 45 62
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-white font-semibold mb-4">Suivez-nous</p>
                  <div className="flex gap-4">
                    <a
                      href="https://www.linkedin.com/company/flowkraft-agency/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-flowkraft-secondary rounded-full flex items-center justify-center transition-all duration-300"
                      aria-label="LinkedIn"
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/flowkraft_agency?igsh=MWZmM2huZmd1azdydg=="
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-flowkraft-secondary rounded-full flex items-center justify-center transition-all duration-300"
                      aria-label="Instagram"
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>
                    <a
                      href="https://web.facebook.com/flowkraftagency"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-flowkraft-secondary rounded-full flex items-center justify-center transition-all duration-300"
                      aria-label="Facebook"
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                    <a
                      href="https://github.com/FlowKraft-Agency"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-flowkraft-secondary rounded-full flex items-center justify-center transition-all duration-300"
                      aria-label="GitHub"
                    >
                      <svg
                        className="w-5 h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form
                className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8"
                onSubmit={handleSubmit}
              >
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-white font-semibold mb-2"
                    >
                      Nom complet
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-flowkraft-secondary"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-white font-semibold mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-flowkraft-secondary"
                      placeholder="jean@exemple.fr"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-white font-semibold mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows={4}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-flowkraft-secondary resize-none"
                      placeholder="Décrivez votre projet..."
                    />
                  </div>

                  {formStatus === "sending" && (
                    <div className="bg-blue-50 bg-opacity-20 border-2 border-blue-400 text-white px-4 py-3 rounded-lg">
                      <p className="font-semibold">Envoi en cours...</p>
                      <p className="text-sm">Veuillez patienter.</p>
                    </div>
                  )}

                  {formStatus === "success" && (
                    <div className="bg-green-50 bg-opacity-20 border-2 border-green-400 text-white px-4 py-3 rounded-lg">
                      <p className="font-semibold">
                        Message envoyé avec succès !
                      </p>
                      <p className="text-sm">
                        Nous vous répondrons dans les plus brefs délais.
                      </p>
                    </div>
                  )}

                  {formStatus === "error" && (
                    <div className="bg-red-50 bg-opacity-20 border-2 border-red-400 text-white px-4 py-3 rounded-lg">
                      <p className="font-semibold">Erreur !</p>
                      <p className="text-sm">
                        Impossible d'envoyer le message. Veuillez réessayer.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-flowkraft-secondary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={formStatus === "sending"}
                  >
                    {formStatus === "sending"
                      ? "Envoi en cours..."
                      : "Envoyer le message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FlowKraftWebsite;
